import { useEffect, useState } from 'react';
import { History, Loader2, UserRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import apiService from '../../services/api';
import equipmentService from '../../services/equipment';
import type { Equipment, EquipmentAssignment } from '../../types/equipment';
import {
  formatDateInTz, formatLocalDateInTz, getTodayInTz, useOrgTimezone,
} from '../../utils/timezone';
import { cn } from '../../utils/cn';

interface OrgUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  username?: string | null;
}

function userLabel(u: OrgUser): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return name || u.username || u.email || `User #${u.id}`;
}

interface EquipmentCustodyCardProps {
  equipment: Equipment;
  /** vehicles creator or admin seat — issuing and taking back are theirs. */
  canAssign: boolean;
  /** Called after an issue, hand-over or return, so the page can re-read. */
  onChanged: () => void;
}

/**
 * Who has this machine. A standing assignment ("Yours until returned") or a
 * loan with a date it is due back — the same record either way, the date is
 * the difference.
 *
 * Custody is shown to everyone and changed only by the register's managers.
 * It is a record, not a lock: the job picker shows the holder but still lets
 * anyone choose the machine.
 */
export default function EquipmentCustodyCard({ equipment, canAssign, onChanged }: EquipmentCustodyCardProps) {
  const orgTz = useOrgTimezone();
  const [history, setHistory] = useState<EquipmentAssignment[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [userId, setUserId] = useState<number | ''>('');
  const [isLoan, setIsLoan] = useState(false);
  const [dueBack, setDueBack] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eqId = equipment.id;
  const holderId = equipment.assigned_user_id ?? null;

  useEffect(() => {
    equipmentService.getAssignments(eqId)
      .then(res => { if (res.success && res.data) setHistory(res.data); })
      .catch(() => { /* history is optional */ });
  }, [eqId, holderId]);

  function openForm() {
    setError(null);
    setUserId('');
    setIsLoan(false);
    setDueBack('');
    setNotes('');
    setEditing(true);
    if (users.length === 0) {
      // Same source and unwrapping as the job assigner (JobUserAssigner).
      apiService.get<{ users?: OrgUser[] } | OrgUser[]>('/users')
        .then(res => {
          if (!res.success) { setError('Could not load the staff list.'); return; }
          const d = res.data;
          setUsers(Array.isArray(d) ? d : d?.users ?? []);
        })
        .catch(() => setError('Could not load the staff list.'));
    }
  }

  async function submit() {
    if (!userId) { setError('Choose who it is going to.'); return; }
    if (isLoan && !dueBack) { setError('A loan needs the date it is due back.'); return; }
    setBusy(true);
    setError(null);
    try {
      await equipmentService.assign(eqId, {
        user_id: Number(userId),
        due_back_on: isLoan ? dueBack : null,
        notes: notes.trim() || null,
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not issue it.');
    } finally {
      setBusy(false);
    }
  }

  async function markReturned() {
    if (!equipment.assignment_id) return;
    setBusy(true);
    setError(null);
    try {
      await equipmentService.returnAssignment(equipment.assignment_id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark it returned.');
    } finally {
      setBusy(false);
    }
  }

  const today = getTodayInTz(orgTz);
  const overdue = !!equipment.assigned_due_back_on && equipment.assigned_due_back_on < today;
  const past = history.filter(h => h.returned_at);

  // Nothing to show and nothing the reader could do: stay out of the way.
  if (!holderId && !canAssign && past.length === 0) return null;

  const inputClass = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UserRound className="h-4 w-4 text-violet-600" />
          Issued to
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {holderId ? (
          <div className="space-y-0.5">
            <p className="font-medium text-neutral-800 break-words">{equipment.assigned_user_name ?? 'Unknown person'}</p>
            <p className="text-sm text-neutral-500">
              Since {equipment.assigned_at ? formatDateInTz(equipment.assigned_at, orgTz) : '—'}
              {' · '}
              {equipment.assigned_due_back_on ? (
                <span className={cn(overdue && 'text-red-600 font-medium')}>
                  {overdue ? 'overdue — was due back ' : 'due back '}
                  {formatLocalDateInTz(equipment.assigned_due_back_on, orgTz)}
                </span>
              ) : (
                'until returned'
              )}
            </p>
          </div>
        ) : (
          <p className="text-sm text-neutral-500">Not issued to anyone — it lives on the register.</p>
        )}

        {canAssign && !editing && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={openForm} disabled={busy}>
              {holderId ? 'Hand over' : 'Issue'}
            </Button>
            {holderId && (
              <Button size="sm" variant="outline" onClick={markReturned} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mark returned'}
              </Button>
            )}
          </div>
        )}

        {editing && (
          <div className="space-y-3 border-t border-neutral-100 pt-3">
            <div>
              <label htmlFor="custody-user" className="block text-xs font-medium text-neutral-500 mb-1">Issue to</label>
              <select
                id="custody-user"
                value={userId}
                onChange={e => setUserId(e.target.value ? Number(e.target.value) : '')}
                className={inputClass}
              >
                <option value="">— Choose a person —</option>
                {users
                  .filter(u => u.id !== holderId)
                  .map(u => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>

            <fieldset className="space-y-1.5">
              <legend className="text-xs font-medium text-neutral-500 mb-1">For how long</legend>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="custody-term" checked={!isLoan} onChange={() => setIsLoan(false)} />
                Until returned (theirs to keep)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="custody-term" checked={isLoan} onChange={() => setIsLoan(true)} />
                A loan, due back on a date
              </label>
              {isLoan && (
                <input
                  type="date"
                  aria-label="Due back on"
                  value={dueBack}
                  min={today}
                  onChange={e => setDueBack(e.target.value)}
                  className={inputClass}
                />
              )}
            </fieldset>

            <div>
              <label htmlFor="custody-notes" className="block text-xs font-medium text-neutral-500 mb-1">Notes (optional)</label>
              <input
                id="custody-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. spare line and a litre of 2-stroke"
                className={inputClass}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={submit} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : holderId ? 'Hand over' : 'Issue'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {past.length > 0 && (
          <div className="border-t border-neutral-100 pt-2">
            <button
              type="button"
              onClick={() => setShowHistory(v => !v)}
              className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
            >
              <History className="h-3.5 w-3.5" />
              {showHistory ? 'Hide' : 'Show'} previous holders ({past.length})
            </button>
            {showHistory && (
              <ul className="mt-2 space-y-1.5 text-sm">
                {past.map(h => (
                  <li key={h.id} className="text-neutral-600">
                    <span className="font-medium text-neutral-700 break-words">{h.user_name ?? 'Unknown'}</span>
                    {' — '}
                    {formatDateInTz(h.assigned_at, orgTz)} to {formatDateInTz(h.returned_at!, orgTz)}
                    {h.return_notes && <span className="text-neutral-400"> · {h.return_notes}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
